import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBackRounded, CheckCircleRounded, LockOpenRounded } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import {
  getAvailableAssessments,
  getStudentAssessment,
  getAssessmentResult,
  submitAssessment,
} from '../services/api';
import {
  AvailableAssessment,
  StudentAssessmentDetail,
  AssessmentResult,
} from '../types';

/** Result view for one graded submission. */
const ResultView: React.FC<{ result: AssessmentResult; onBack: () => void }> = ({ result, onBack }) => (
  <div className="mx-auto w-full max-w-2xl px-4 py-6">
    <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
      <ArrowBackRounded fontSize="small" /> Back
    </button>
    <div className="mb-5 rounded-2xl bg-violet-600 p-5 text-white">
      <p className="text-sm opacity-90">{result.title}</p>
      <p className="text-3xl font-bold">{result.total_score}%</p>
      <p className="text-xs opacity-90">
        MCQ {result.mcq_score ?? '—'}% · Written {result.open_score ?? '—'}%
      </p>
    </div>
    <div className="flex flex-col gap-3">
      {result.results.map((r) => (
        <div key={r.question_id} className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-sm font-medium text-slate-800">{r.question}</p>
          {r.kind === 'mcq' ? (
            <p className={`mt-1 text-sm ${r.correct ? 'text-emerald-600' : 'text-rose-600'}`}>
              {r.correct ? 'Correct' : 'Not quite'} · {r.explanation}
            </p>
          ) : (
            <div className="mt-1">
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-violet-700">Score: {r.score}%</span> — {r.feedback}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

/** The taker for one assessment. */
const Taker: React.FC<{
  assessment: StudentAssessmentDetail;
  onBack: () => void;
  onGraded: (r: AssessmentResult) => void;
}> = ({ assessment, onBack, onGraded }) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = useMemo(
    () => assessment.sections.reduce((n, s) => n + s.questions.length, 0),
    [assessment]
  );
  const answered = Object.values(answers).filter((v) => (v || '').trim()).length;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitAssessment(assessment.id, answers);
      onGraded(result);
    } catch (e: any) {
      setError(e.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6">
      <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowBackRounded fontSize="small" /> Back
      </button>
      <h1 className="text-lg font-bold text-slate-900">{assessment.title}</h1>
      <p className="mb-5 text-sm text-slate-500">
        {assessment.topics.map((t) => t.topic_title).join(', ')}
      </p>

      <div className="flex flex-col gap-6">
        {assessment.sections.map((section, si) => (
          <div key={section.key}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-600">
              Section {si + 1} · {section.title}
            </p>
            <div className="flex flex-col gap-4">
              {section.questions.map((q) => (
                <div key={q.id} className="rounded-xl border border-slate-200 bg-white p-3">
                  <p className="text-sm font-medium text-slate-800">{q.question}</p>
                  {q.kind === 'mcq' && q.options ? (
                    <div className="mt-2 flex flex-col gap-1.5">
                      {q.options.map((opt) => (
                        <label key={opt.id} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name={q.id}
                            checked={answers[q.id] === opt.id}
                            onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt.id }))}
                          />
                          {opt.text}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      className="mt-2 w-full resize-y rounded-lg border border-slate-300 p-2 text-sm"
                      rows={3}
                      placeholder="Write your answer…"
                      value={answers[q.id] || ''}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
      <div className="sticky bottom-0 mt-6 flex items-center justify-between gap-3 bg-gray-50/90 py-3">
        <span className="text-xs text-slate-500">
          {answered}/{total} answered
        </span>
        <button
          onClick={submit}
          disabled={submitting}
          className="flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
        >
          {submitting && <CircularProgress size={16} sx={{ color: 'white' }} />}
          {submitting ? 'Grading your answers…' : 'Submit for grading'}
        </button>
      </div>
    </div>
  );
};

const AssessmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<AvailableAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<StudentAssessmentDetail | null>(null);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await getAvailableAssessments());
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const open = async (a: AvailableAssessment) => {
    setBusyId(a.id);
    setError(null);
    try {
      if (a.completed) {
        setResult(await getAssessmentResult(a.id));
      } else {
        setActive(await getStudentAssessment(a.id));
      }
    } catch (e: any) {
      setError(e.message || 'Failed to open assessment');
    } finally {
      setBusyId(null);
    }
  };

  const backToList = () => {
    setActive(null);
    setResult(null);
    loadList();
  };

  if (result) return <ResultView result={result} onBack={backToList} />;
  if (active) return <Taker assessment={active} onBack={backToList} onGraded={setResult} />;

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-6">
      <button onClick={() => navigate('/chat')} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowBackRounded fontSize="small" /> Back to chat
      </button>
      <h1 className="text-xl font-bold text-slate-900">Your Assessments</h1>
      <p className="mb-5 text-sm text-slate-500">
        Assessments unlock once you finish a module with the coach.
      </p>

      {loading ? (
        <p className="py-10 text-center text-slate-500">Loading…</p>
      ) : error ? (
        <p className="py-10 text-center text-rose-600">{error}</p>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-500">
          <LockOpenRounded className="mb-2 text-slate-300" />
          <p className="text-sm">No assessments yet — finish a module to unlock one!</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {list.map((a) => (
            <button
              key={a.id}
              onClick={() => open(a)}
              disabled={busyId === a.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-[#E9E6F4] bg-white p-4 text-left hover:border-violet-300 disabled:opacity-60"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{a.title}</p>
                <p className="text-xs text-slate-400">
                  {a.topics.length} topic{a.topics.length !== 1 ? 's' : ''} · {a.question_count} questions
                </p>
              </div>
              {a.completed ? (
                <span className="flex flex-none items-center gap-1 text-xs font-semibold text-emerald-600">
                  <CheckCircleRounded fontSize="inherit" /> {a.total_score}%
                </span>
              ) : (
                <span className="flex-none rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white">
                  {busyId === a.id ? 'Opening…' : 'Start'}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentsPage;
